import * as React from "react";
import {IUserTips} from "../../modules/types";
import {IMatch} from "./../../modules/reducers/gameweeks";
import {map, bindAll, reduce, isNaN, debounce, filter, size} from "lodash";
import {SquadAvatar, Exist} from "../";
import tab_logo from "../../assets/img/sponsor/tab.svg";
import pokerstarsLogo from "../../assets/img/sponsor/pokerstars_white.png";
import {
	formatOdds,
	convertDecimalToAmerican,
	germanOddsStr,
	getCurrencyLabel,
	isMobile,
	ODDS_PARTNER_KEY,
} from "../../modules/utils";
import {
	BigLabel,
	DescriptionDiv,
	FlexibleInputsDiv,
	FlexibleDiv,
	LogosDiv,
	LinkOut,
	OddsGeneratorheaderDiv,
	OddsGeneratorWrapperDiv,
	ErrorMessageDiv,
	OddsValueDiv,
	OddsWrapperDiv,
	MultiWrapperDiv,
	PlaceBetDisabledButton,
	SmallLabel,
	SeparatorDiv,
	TeamInfoDiv,
	TipItemDiv,
	TipItemWrapperDiv,
	TipsWrapperDiv,
	TitleH5,
	Disclaimer,
} from "./MultiGeneratorStyles";
import {Syndicates} from "modules/utils/enums";
import bwinLogo from "assets/img/sponsor/bwin.png";
import {Fragment} from "react";

const GAME_NAME = process.env.REACT_APP_GAME_NAME || "";
const FIXED_NUM = 2;
const BWIN_FALLBACK_LINK = "https://www.bwin.de/de/sports/american-football-11";

interface IProps {
	readonly tips: IUserTips[] | [];
}

interface IState {
	stake: number;
	is_mobile: boolean;
	is_stake_touched: boolean;
	is_bet_attempted: boolean;
}

const getLogo = (gameName: string) => {
	const logoSrc =
		{
			Tabcorp: tab_logo,
			prosieben: pokerstarsLogo,
			RTL: bwinLogo,
		}[gameName] || "";

	return logoSrc.length ? <img src={logoSrc} alt={gameName} /> : null;
};

const getLink = (gameName: string, oddsPropIds: string) => {
	return {
		Tabcorp: `//ad.doubleclick.net/ddm/clk/451489820;255503323;e?https://www.tab.com.au/sports/betting/American%20Football/competitions/NFL?propId=${oddsPropIds}`,
		prosieben: `https://www.skybet.de/?addAffiliateSelectionId=${oddsPropIds}`,
		Mexico: "https://gtly.to/chjXHl5wF",
		RTL: BWIN_FALLBACK_LINK,
	}[gameName];
};

const toBwinOption = (oddsId: string) => {
	const parts = oddsId.split(",").map((part) => part.trim());
	return parts.length === 3 && parts.every(Boolean) ? parts.join("-") : "";
};

const getBwinLink = (oddsIds: string[], stake: number) => {
	const options = oddsIds.map(toBwinOption);

	if (!options.length || options.some((option) => !option)) {
		return BWIN_FALLBACK_LINK;
	}

	const params = [
		`options=${options.join(",")}`,
		`type=${options.length > 1 ? "combo" : "single"}`,
		`stake=${stake}`,
	];

	return `https://www.bwin.de/de/sports?${params.join("&")}`;
};

export class MultiOddsGeneratorComponent extends React.PureComponent<
	IProps,
	IState
> {
	public state = {
		stake: 10,
		is_mobile: isMobile(),
		is_stake_touched: false,
		is_bet_attempted: false,
	};
	private onResizeDebounced?: () => void;

	constructor(props: IProps, state: IState) {
		super(props, state);
		bindAll(this, ["setStake", "onResize", "onBlockedBetClick"]);
	}
	/**
	 * @ignore
	 */
	public componentDidMount(): void {
		const debounce_time = 100;
		this.onResizeDebounced = debounce(this.onResize, debounce_time);
		window.addEventListener("resize", this.onResizeDebounced);
	}

	/**
	 * @ignore
	 */
	public render() {
		if (
			!["Tabcorp", "Mexico", "UK", "FR", "RTL"].includes(GAME_NAME) ||
			!this.is_odds
		) {
			return null;
		}
		const {is_mobile} = this.state;
		const AVATAR_WIDTH = 20;
		const is_bet_blocked = this.is_bet_blocked;
		const error_message = this.error_message;

		const formattedOddsValue =
			GAME_NAME === "Mexico"
				? convertDecimalToAmerican(this.odds_value)
				: this.odds_value.toFixed(2);

		return (
			<MultiWrapperDiv>
				<OddsGeneratorheaderDiv>
					<LogosDiv>{getLogo(GAME_NAME)}</LogosDiv>
					<TitleH5>
						{window.getTranslations("pickem_betting_calculator")}
					</TitleH5>
					<DescriptionDiv>
						{window.getTranslations("pickem_betting_text_multi")}
					</DescriptionDiv>
				</OddsGeneratorheaderDiv>
				<OddsGeneratorWrapperDiv>
					<TipsWrapperDiv>
						{map(this.odds_tips, (tip) => (
							<TipItemWrapperDiv key={tip.id}>
								<TipItemDiv
									is_active={
										tip.predict_winner_squad_id ===
										tip.away_squad_id
									}
								>
									<OddsValueDiv
										is_active={
											tip.predict_winner_squad_id ===
											tip.away_squad_id
										}
									>
										{getCurrencyLabel(true)}
										{formatOdds(tip.away_squad_odds)}
									</OddsValueDiv>

									<TeamInfoDiv>
										{is_mobile
											? tip.away_squad_short_name
											: tip.away_squad_name}
										<SquadAvatar
											alt={tip.away_squad_name}
											squad_id={tip.away_squad_id}
											margin="0 20px"
											width={AVATAR_WIDTH}
										/>
									</TeamInfoDiv>
								</TipItemDiv>
								<SeparatorDiv>
									{window.getTranslations(
										"pickem_betting_at"
									)}
								</SeparatorDiv>
								<TipItemDiv
									is_active={
										tip.predict_winner_squad_id ===
										tip.home_squad_id
									}
								>
									<TeamInfoDiv>
										<SquadAvatar
											alt={tip.home_squad_name}
											squad_id={tip.home_squad_id}
											margin="0 20px"
											width={AVATAR_WIDTH}
										/>
										{is_mobile
											? tip.home_squad_short_name
											: tip.home_squad_name}
									</TeamInfoDiv>

									<OddsValueDiv
										is_active={
											tip.predict_winner_squad_id ===
											tip.home_squad_id
										}
									>
										{getCurrencyLabel(true)}
										{formatOdds(tip.home_squad_odds)}
									</OddsValueDiv>
								</TipItemDiv>
							</TipItemWrapperDiv>
						))}

						<OddsWrapperDiv>
							<BigLabel>
								{window.getTranslations(
									"pickem_betting_text_stake"
								)}
							</BigLabel>

							<FlexibleInputsDiv>
								<input
									type="text"
									value={`${getCurrencyLabel()}${
										this.state.stake
									}`}
									maxLength={10}
									onChange={this.setStake}
									name="stake"
								/>
								<Exist when={!is_bet_blocked}>
									<LinkOut
										href={this.bet_link}
										target="_blank"
										rel="noopener noreferrer"
										is_active={true}
									>
										{window.getTranslations(
											"pickem_betting_text_place"
										)}
									</LinkOut>
								</Exist>
								<Exist when={is_bet_blocked}>
									<PlaceBetDisabledButton
										type="button"
										aria-disabled={true}
										onClick={this.onBlockedBetClick}
									>
										{window.getTranslations(
											"pickem_betting_text_place"
										)}
									</PlaceBetDisabledButton>
								</Exist>
							</FlexibleInputsDiv>
							<Exist when={!!error_message}>
								<ErrorMessageDiv role="alert">
									{error_message}
								</ErrorMessageDiv>
							</Exist>
							<Exist when={GAME_NAME === "prosieben"}>
								<FlexibleDiv>
									<SmallLabel>
										{this.odds_value
											? germanOddsStr(
													`${getCurrencyLabel()}${this.odds_value.toFixed(
														2
													)}`
											  )
											: "NA"}
									</SmallLabel>
								</FlexibleDiv>
							</Exist>
							<Exist when={GAME_NAME !== "prosieben"}>
								<FlexibleDiv>
									<div>
										<SmallLabel>
											<Exist
												when={
													!!ODDS_PARTNER_KEY?.length
												}
											>
												<Fragment>
													{window.getTranslations(
														ODDS_PARTNER_KEY ||
															"pickem_betting_text_partner_odds"
													)}
													:
												</Fragment>
											</Exist>
											<Exist
												when={!ODDS_PARTNER_KEY?.length}
											>
												&nbsp;
											</Exist>
										</SmallLabel>
										<p>
											{this.odds_value
												? `${getCurrencyLabel(
														true
												  )}${formattedOddsValue}`
												: "NA"}
										</p>
									</div>
									<div>
										<SmallLabel>
											{window.getTranslations(
												"pickem_betting_text_estimated"
											)}
											:
										</SmallLabel>
										<p>{this.estimated_return}</p>
									</div>
									<div>
										<SmallLabel>
											{window.getTranslations(
												this.gamble_key
											)}
										</SmallLabel>
									</div>
								</FlexibleDiv>
							</Exist>
						</OddsWrapperDiv>
					</TipsWrapperDiv>
					<Exist when={GAME_NAME === Syndicates.DE_RTL}>
						<Disclaimer>
							{window.getTranslations(
								"odds_disclaimer_text",
								[],
								"Quotenänderungen vorbehalten | Lizenziert (Whitelist) | 18+ | Suchtrisiko | Hilfe unter "
							)}
							<a
								href="https://www.bundesweit-gegen-gluecksspielsucht.de/"
								target="_blank"
								rel="noreferrer noopener"
							>
								{window.getTranslations(
									"odds_disclaimer_link",
									[],
									"Bundesweite Anlaufstelle - Die Landeskoordinierungsstellen Glücksspielsucht"
								)}
							</a>
						</Disclaimer>
					</Exist>
				</OddsGeneratorWrapperDiv>
			</MultiWrapperDiv>
		);
	}

	private get is_valid_stake() {
		return this.state.stake > 0;
	}

	private get is_bet_blocked() {
		return !this.odds_prop_ids || !this.is_valid_stake;
	}

	private get error_message() {
		const {is_stake_touched, is_bet_attempted} = this.state;

		if (!this.odds_prop_ids && is_bet_attempted) {
			return window.getTranslations(
				"pickem_betting_error_no_picks",
				[],
				"Select at least one winner to place a bet."
			);
		}

		if (!this.is_valid_stake && (is_stake_touched || is_bet_attempted)) {
			return window.getTranslations(
				"pickem_betting_error_invalid_stake",
				[],
				"Enter a valid stake greater than 0."
			);
		}

		return "";
	}

	private get gamble_key() {
		return GAME_NAME === Syndicates.AU
			? "pickem_betting_text_gamble_responsibly_tab"
			: "pickem_betting_text_gamble_responsibly";
	}

	private get odds_tips() {
		const {tips} = this.props;
		return tips.filter(
			(tip: IMatch) =>
				tip.status === "scheduled" &&
				tip.home_squad_odds > 0 &&
				tip.away_squad_odds > 0
		);
	}

	private get is_odds() {
		if (this.odds_tips[0] && this.odds_tips[0].status !== "scheduled") {
			return false;
		}
		return (
			size(
				filter(
					this.odds_tips,
					(tip) => tip.away_squad_odds && tip.home_squad_odds
				)
			) > 0
		);
	}

	private get odds_value() {
		const acc_def = 1;
		const odds_value = reduce(
			this.odds_tips,
			(acc, item) => {
				const {
					predict_winner_squad_id,
					home_squad_id,
					away_squad_id,
					home_squad_odds,
					away_squad_odds,
				} = item;

				if (
					predict_winner_squad_id === home_squad_id &&
					home_squad_odds
				) {
					return home_squad_odds * acc;
				}

				if (
					predict_winner_squad_id === away_squad_id &&
					away_squad_odds
				) {
					return away_squad_odds * acc;
				}

				return acc;
			},
			1
		);

		return odds_value === acc_def ? 0 : odds_value;
	}

	private get estimated_return() {
		const estimated_return = this.odds_value * this.state.stake;
		return isNaN(estimated_return) || estimated_return === 0
			? "NA"
			: `${getCurrencyLabel()}${estimated_return.toFixed(FIXED_NUM)}`;
	}

	private get bet_link() {
		if (GAME_NAME === Syndicates.DE_RTL) {
			return getBwinLink(this.selected_odds_ids, this.state.stake);
		}

		return getLink(GAME_NAME, this.odds_prop_ids);
	}

	private get odds_prop_ids() {
		return this.selected_odds_ids.join(",");
	}

	private get selected_odds_ids() {
		return this.odds_tips
			.map((item) => {
				const {
					predict_winner_squad_id,
					home_squad_id,
					away_squad_id,
					home_squad_odds_id,
					away_squad_odds_id,
				} = item;

				if (predict_winner_squad_id === home_squad_id) {
					return home_squad_odds_id;
				}

				if (predict_winner_squad_id === away_squad_id) {
					return away_squad_odds_id;
				}

				return "";
			})
			.filter((item) => !!item.length);
	}

	private setStake(e: React.SyntheticEvent<HTMLInputElement>) {
		e.preventDefault();
		const {value} = e.currentTarget;
		const currency_label = getCurrencyLabel();
		const amount = currency_label.length
			? value.split(currency_label).join("")
			: value;
		const stake = Number(amount);

		this.setState({
			stake: isNaN(stake) ? 0 : stake,
			is_stake_touched: true,
		});
	}

	private onBlockedBetClick() {
		this.setState({
			is_bet_attempted: true,
		});
	}

	private onResize() {
		const is_mobile = isMobile();

		this.setState({
			is_mobile,
		});
	}
}

export const MultiOddsGenerator = MultiOddsGeneratorComponent;

export default MultiOddsGenerator;

export * from "./MultiOddsGeneratorUK";
